import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { getMedicationFullName, getMedicationInn, getMedicationUniqueForms } from '@lib/prescriptions/data-access';
import { combineReasons } from '@lib/prescriptions/data-access';
import { adaptTrademarksFromPrescription } from '@lib/prescriptions/data-access';
import { PHARMACIES_LATITUDE, PHARMACIES_LONGITUDE, PHARMACIES_MAX_SEARCH_DISTANCE_METERS } from '@lib/prescriptions/data-access';
import { PrescriptionsService } from '@lib/prescriptions/data-access';
import { addPrescriptionDraft, updatePrescriptionDraft } from '@lib/prescriptions/data-access';
import { reasonNotValid } from '@lib/prescriptions/data-access';
import { APPROVE_REASON, DECLINE_REASONS, EDIT_REASONS, GuaranteeStatus } from '@lib/prescriptions/data-access';
import {
  DRUGS_FREQUENCY,
  PharmacologicalItemMetaData,
  PharmacologicalTreatmentControls,
  Prescription,
  PrescriptionControls,
  PrescriptionItem,
  PrescriptionStatus,
  PrescriptionTypes,
} from '@lib/prescriptions/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, catchError, combineLatest, concatMap, debounceTime, filter, map, merge, of, startWith, switchMap, take, tap } from 'rxjs';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';

import { adaptPlainObjectToCamelCase } from '@lib/commons/util-functions/plain-object-to-pascal.function';

import { LiloDrugSearchResponse, MedicationBasketRequest } from '@lib/core/models/protocol.interface';

import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

const MEDICATION_MIN_PERIOD: number = 5;

@Component({
  selector: 'prescriptions-medication-prescription-form-modal',
  standalone: true,
  imports: [
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIcon,
    MatInput,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    MatTooltip,
    MatTabGroup,
    MatTab,
    MatCheckboxModule,
    MatDatepickerModule,
    LoaderComponent,
    SpinLoadingComponent,
    TranslatePipe,
    NgClass,
  ],
  templateUrl: './medication-prescription-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedicationPrescriptionFormModalComponent implements OnInit {
  private readonly data: {
    prescription: Prescription | null;
    prescriptionType: PrescriptionTypes;
    memberId: string;
    guaranteeApplicationId: string;
    pharmaciesCount: number;
  } = inject(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<MedicationPrescriptionFormModalComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly prescriptionsService = inject(PrescriptionsService);
  private readonly store = inject(Store);

  protected readonly guaranteeStatus = GuaranteeStatus;
  protected readonly declineReasons = DECLINE_REASONS;
  protected readonly editReasons = EDIT_REASONS;
  protected readonly drugsFrequencyOptions = DRUGS_FREQUENCY;
  protected readonly guaranteeStatusOptions = [this.guaranteeStatus.Guaranteed, this.guaranteeStatus.NotGuaranteed, this.guaranteeStatus.Auto];

  protected readonly medication = signal<Prescription>(this.data.prescription);
  protected readonly memberId = signal<string>(this.data.memberId);
  protected readonly guaranteeApplicationId = signal<string>(this.data.guaranteeApplicationId);
  protected readonly preselectedPharmaciesCount = signal<number>(this.data.pharmaciesCount);

  protected readonly loading = signal<boolean>(true);
  protected readonly statusChanged = signal<boolean>(false);
  protected readonly dataChanged = signal<boolean>(false);
  protected readonly completionChecked = signal<boolean>(false);
  protected readonly loadingPharmaciesCount = signal<boolean>(false);
  protected readonly pharmaciesCount = signal<number>(null);
  protected readonly innsOptions = signal<string[]>([]);
  protected readonly formsOptions = signal<string[]>([]);
  protected readonly trademarksOptions = signal<LiloDrugSearchResponse[]>([]);
  protected readonly showTrademarksInfo = signal<boolean>(false);
  protected readonly filteredDeclineReasons = signal<string[]>(this.declineReasons);
  protected readonly filteredEditReasons = signal<string[]>(this.editReasons);
  protected readonly franchiseInitialValue = signal<number>(0);
  protected readonly mappingInvalid = signal<boolean>(false);

  protected medicationForm!: FormGroup<PrescriptionControls<PharmacologicalTreatmentControls>>;
  protected startDate = new Date();
  protected endDate = new Date(this.startDate);
  protected readonly pharmaciesCountCalculated$ = new BehaviorSubject<typeof this.medicationForm.value>(null);

  protected get fc(): PrescriptionControls<PharmacologicalTreatmentControls> {
    return this.medicationForm?.controls;
  }

  protected get metadataFc(): PharmacologicalTreatmentControls {
    return this.medicationForm.controls.metadata.controls;
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.initPharmaciesCountWatcher();
    this.presetPharmaciesCount();
  }

  private initFormGroup() {
    this.medicationForm = this.fb.group<PrescriptionControls<PharmacologicalTreatmentControls>>({
      type: this.fb.control<PrescriptionTypes | null>(PrescriptionTypes.Drugs),
      name: this.fb.control<string | null>(null, [Validators.required]),
      startDate: this.fb.control<Date | null>(this.startDate, [Validators.required]),
      endDate: this.fb.control<Date | null>(this.getEndDate(), [Validators.required]),
      metadata: this.fb.group<PharmacologicalTreatmentControls>({
        form: this.fb.control<string[] | null>(null, [Validators.required]),
        singleDose: this.fb.control<number | null>(null, [Validators.required, Validators.min(1)]),
        frequency: this.fb.control<string | null>(null, [Validators.required]),
        treatmentDose: this.fb.control<number | null>(null, [Validators.required, Validators.min(1)]),
        treatmentDoseConfirmation: this.fb.control<boolean | null>(false, [Validators.requiredTrue]),
        dailyAmount: this.fb.control<number | null>(null, [Validators.required]),
        duration: this.fb.control<number | null>(null, [Validators.required, Validators.min(1)]),
        inn: this.fb.control<string | null>(null, [Validators.required]),
        morionSubstance: this.fb.control<string | null>(null, [Validators.required]),
        morionItems: this.fb.array<FormControl<LiloDrugSearchResponse | null>>([], [Validators.required]),
      }),
      guaranteeStatus: this.fb.control<string | null>(null, [Validators.required]),
      editReason: this.fb.control<string | null>(null),
      statusReason: this.fb.control<string>(null),
      sendGuaranteeNotification: this.fb.control<boolean>(true),
      franchise: this.fb.control<number>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      franchiseManualChangeConfirmation: this.fb.control<boolean>(true, [Validators.requiredTrue]),
      completedInAppointment: this.fb.control<boolean>(false),
      originalPrescriptionId: this.fb.control<string | null>(null),
    });
    this.watchAllDataChanges();
    this.watchFormPharmaciesChanges();
    this.watchStatusChanges();
    this.watchCompletionCheckboxChanges();
    this.watchDeclineReasonsChanges();
    this.watchEditReasonsChanges();
    this.calculateTreatmentDose();
    if (this.medication()) {
      this.patchMedicationForm(this.medication());
      this.mappingInvalid.set(this.isMedicationMappingInvalid(this.medication().metadata as PharmacologicalItemMetaData));
      this.watchMappingValidity();
    } else {
      this.statusChanged.set(true);
      this.fc.statusReason.setValidators([Validators.required, reasonNotValid(this.declineReasons)]);
      this.fc.statusReason.updateValueAndValidity({ emitEvent: false });
    }
    this.loading.set(false);
    this.fetchFranchise();
  }

  private isMedicationMappingInvalid(metadata: PharmacologicalItemMetaData): boolean {
    const innMissing = !metadata?.inn || metadata.inn.trim().length === 0;
    const formMissing = !metadata?.form || metadata.form.trim().length === 0;
    const morionRaw = metadata?.morion_items?.trim();
    const morionMissing = !morionRaw || morionRaw === '[]';
    return innMissing || formMissing || morionMissing;
  }

  private watchMappingValidity(): void {
    merge(this.metadataFc.inn.valueChanges, this.metadataFc.form.valueChanges, this.metadataFc.morionItems.valueChanges)
      .pipe(
        tap(() => {
          const inn = this.metadataFc.inn.value;
          const form = this.metadataFc.form.value;
          const items = this.metadataFc.morionItems.value;
          const innMissing = !inn || (typeof inn === 'string' && inn.trim().length === 0);
          const formMissing = !form || form.length === 0;
          const itemsMissing = !items || items.length === 0;
          this.mappingInvalid.set(innMissing || formMissing || itemsMissing);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private patchMedicationForm(medication: Prescription) {
    const medicationMetadata: PharmacologicalItemMetaData = medication.metadata as PharmacologicalItemMetaData;
    if (medicationMetadata.morion_items) {
      this.trademarksOptions.set(adaptTrademarksFromPrescription(JSON.parse(medicationMetadata.morion_items)));
    } else {
      this.trademarksOptions.set(JSON.parse(medicationMetadata.trademarks));
    }
    const inn = getMedicationInn(medicationMetadata);
    const uniqueForms = getMedicationUniqueForms(medicationMetadata);
    this.innsOptions.set([inn]);
    this.formsOptions.set(uniqueForms);
    this.medicationForm.patchValue(
      {
        type: medication.type,
        name: getMedicationFullName(medicationMetadata),
        startDate: new Date(medication.startDate),
        endDate: new Date(medication.endDate),
        metadata: {
          inn: this.innsOptions()[0],
          form: this.formsOptions(),
          treatmentDoseConfirmation: true,
          morionSubstance: medicationMetadata.morion_substance || medicationMetadata.inn,
          morionItems: medicationMetadata.morion_items ? adaptPlainObjectToCamelCase(JSON.parse(medicationMetadata.morion_items)) : JSON.parse(medicationMetadata.trademarks),
        },
        guaranteeStatus: medication.guaranteeStatus as string,
        franchise: medication.franchise || 0,
        originalPrescriptionId: medication.originalPrescriptionId,
      },
      { emitEvent: false }
    );
    this.fc.metadata.patchValue({
      singleDose: +medicationMetadata.single_dose || 1,
      frequency: +medicationMetadata.frequency ? medicationMetadata.frequency : '1',
      duration: +medicationMetadata.duration || 1,
      dailyAmount: +medicationMetadata.daily_amount,
      treatmentDose: +medicationMetadata.treatment_dose,
    });
    this.franchiseInitialValue.set(medication.franchise || 0);
    this.trademarksOptions().forEach(trademark => {
      this.metadataFc.morionItems.push(new FormControl<LiloDrugSearchResponse | null>(trademark), { emitEvent: false });
    });
    if (this.fc.guaranteeStatus.value === PrescriptionStatus.Auto || this.fc.guaranteeStatus.value === PrescriptionStatus.Guaranteed) {
      this.fc.guaranteeStatus.disable();
    }
  }

  private fetchFranchise() {
    if (this.medication()) {
      return;
    }
    this.loading.set(true);
    this.prescriptionsService
      .fetchPrescriptionFranchiseByMemberId(this.memberId(), {
        requestDate: this.fc.startDate.value,
        dictionaryType: this.fc.type.value,
      })
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.franchise) {
            this.franchiseInitialValue.set(response.franchise);
            this.fc.franchise.setValue(response.franchise, { emitEvent: false });
          }
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected selectInn(inn: string): void {
    this.clearPharmacologicalTreatmentData();
    this.fillFormsOptions(inn);
  }

  protected selectInnByTrademark(option: LiloDrugSearchResponse): void {
    this.clearPharmacologicalTreatmentData();
    const inn = option.identifier.toUpperCase();
    const form = [option.formUa];
    this.fillFormsOptions(inn);
    this.fillMedicationControls(inn, form);
  }

  protected selectMedicationForm(form: string[]): void {
    const inn = this.metadataFc.inn.value;
    this.fillMedicationControls(inn, form);
  }

  protected toggleTrademarksInfoSection(): void {
    this.showTrademarksInfo.set(!this.showTrademarksInfo());
  }

  private fillFormsOptions(inn: string) {
    const innTrademarks = this.trademarksOptions().filter(trademark => {
      return trademark.identifier?.toUpperCase() === inn;
    });
    this.formsOptions.set([...new Set(innTrademarks.map(trademark => trademark.formUa))]);
  }

  private fillMedicationControls(inn: string, form: string[]): void {
    const trademarks: LiloDrugSearchResponse[] = this.trademarksOptions()
      .filter(trademark => trademark.identifier?.toUpperCase() === inn)
      .filter(trademark => form.includes(trademark.formUa));
    this.medicationForm.patchValue(
      {
        name: `${inn} ${form}`,
        metadata: {
          inn: inn,
          form: form,
          morionSubstance: inn,
        },
      },
      { emitEvent: false }
    );
    this.clearTrademarks();
    trademarks.forEach(trademark => this.metadataFc.morionItems.push(new FormControl<LiloDrugSearchResponse | null>(trademark)));
  }

  private clearPharmacologicalTreatmentData(): void {
    this.medicationForm.patchValue({
      name: null,
      metadata: {
        morionSubstance: null,
        form: null,
      },
    });
    this.clearTrademarks();
  }

  private clearTrademarks(): void {
    this.metadataFc.morionItems.clear();
  }

  private presetPharmaciesCount() {
    if (this.preselectedPharmaciesCount()) {
      this.pharmaciesCount.set(this.preselectedPharmaciesCount());
    } else {
      this.updatePharmaciesCount(this.medicationForm.value);
    }
  }

  private updatePharmaciesCount(prescription: typeof this.medicationForm.value) {
    this.pharmaciesCountCalculated$.next(prescription);
  }

  private initPharmaciesCountWatcher() {
    this.pharmaciesCountCalculated$
      .pipe(
        filter(prescription => {
          if (!!prescription && !!prescription.metadata.inn && !!prescription.metadata.form && !!prescription.metadata.treatmentDose) {
            return true;
          }
          this.pharmaciesCount.set(null);
          return false;
        }),
        tap(() => this.loadingPharmaciesCount.set(true)),
        switchMap((prescription: typeof this.medicationForm.value) => {
          const emptyResponse = { pharmacies: [] as unknown[] };
          if (prescription) {
            return this.prescriptionsService.getMedicationBasket(this.createMedicationBasketRequest(prescription)).pipe(catchError(() => of(emptyResponse)));
          }
          return of(emptyResponse);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          if (response.pharmacies?.length >= 0) this.pharmaciesCount.set(response.pharmacies.length);
          else console.error(`No pharmacies were found in ${PHARMACIES_MAX_SEARCH_DISTANCE_METERS} meters.`);
          this.loadingPharmaciesCount.set(false);
        },
        error: () => {
          this.loadingPharmaciesCount.set(false);
          console.error(`Encountered problems during pharmacies search in ${PHARMACIES_MAX_SEARCH_DISTANCE_METERS} meters.`);
        },
      });
  }

  private watchFormPharmaciesChanges(): void {
    merge(this.watchInnChanges(), this.watchMedicationFormChanges(), this.watchTreatmentDoseChanges())
      .pipe(
        debounceTime(300),
        tap(() => {
          this.updatePharmaciesCount(this.medicationForm.value);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private createMedicationBasketRequest(prescription: typeof this.medicationForm.value) {
    const medicationMetadata = prescription.metadata;
    const prescriptionId = crypto.randomUUID();
    const requestBody: MedicationBasketRequest = {
      uniqueId: crypto.randomUUID(),
      products: medicationMetadata.morionItems.map(item => {
        return {
          prescriptionId: prescriptionId,
          code: item.code.toString(),
          identifier: medicationMetadata.morionSubstance || medicationMetadata.inn,
          substanceQuantity: item.substanceQuantity,
          quantityInPackage: item.quantityInPackage,
          form: item.formUa,
          name: item.nameFullUa,
          requiredQuantity: prescription.metadata.treatmentDose,
        };
      }),
      latitude: PHARMACIES_LATITUDE,
      longitude: PHARMACIES_LONGITUDE,
      maxDistanceInMeters: PHARMACIES_MAX_SEARCH_DISTANCE_METERS,
      requireNewFormation: true,
    };
    return requestBody;
  }

  private watchAllDataChanges() {
    merge(
      this.watchPharmacologicalTreatmentSearch(),
      this.watchMedicationFormChanges(),
      this.watchSingleDoseChanges(),
      this.watchFrequencyChanges(),
      this.watchDurationChanges(),
      this.watchDailyAmountChanges(),
      this.watchTreatmentDoseChanges(),
      this.watchStartDateChanges(),
      this.watchEndDateChanges(),
      this.watchFranchiseChanges()
    )
      .pipe(
        tap(() => {
          if (this.medication()) {
            this.fc.editReason.setValidators([Validators.required]);
            this.fc.editReason.updateValueAndValidity();
            this.dataChanged.set(true);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchPharmacologicalTreatmentSearch() {
    return this.metadataFc.inn.valueChanges.pipe(
      debounceTime(500),
      filter(searchString => !!searchString && typeof searchString === 'string' && searchString.trim().length >= 3),
      tap(() => {
        this.clearPharmacologicalTreatmentData();
      }),
      switchMap(searchString => {
        return this.prescriptionsService.searchMedicalTreatment(searchString.split('+')[0].trim()).pipe(
          catchError(() => {
            return of([] as LiloDrugSearchResponse[]);
          })
        );
      }),
      map(trademarks => {
        return trademarks.filter(trademark => !!trademark.identifier);
      }),
      tap(trademarks => {
        this.innsOptions.set([...new Set(trademarks.map(trademark => trademark.identifier.toUpperCase()))]);
        this.trademarksOptions.set(trademarks);
        this.trademarksOptions.update((options: LiloDrugSearchResponse[]) => {
          return options.sort((a, b) => a.nameFullUa.localeCompare(b.nameFullUa));
        });
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchInnChanges() {
    return this.metadataFc.inn.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private watchMedicationFormChanges() {
    return this.metadataFc.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private watchSingleDoseChanges() {
    return this.metadataFc.singleDose.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private watchFrequencyChanges() {
    return this.metadataFc.frequency.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private watchDurationChanges() {
    return this.metadataFc.duration.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private watchDailyAmountChanges() {
    return this.metadataFc.dailyAmount.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private watchTreatmentDoseChanges() {
    return this.metadataFc.treatmentDose.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private watchStartDateChanges() {
    return this.fc.startDate.valueChanges.pipe(
      tap(() => this.fc.endDate.patchValue(this.getEndDate(), { emitEvent: false })),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchEndDateChanges() {
    return this.fc.endDate.valueChanges.pipe(
      concatMap((value: Date) => {
        if (value) {
          value.setHours(23, 59, 59, 999);
        }
        return of(value);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchFranchiseChanges() {
    return this.fc.franchise.valueChanges.pipe(
      tap(value => this.fc.franchiseManualChangeConfirmation.setValue(value === this.franchiseInitialValue())),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchStatusChanges() {
    this.fc.guaranteeStatus.valueChanges
      .pipe(
        tap((value: string) => {
          const statusReasonControl = this.fc.statusReason;
          if (value === this.guaranteeStatus.Guaranteed) {
            statusReasonControl.setValue(APPROVE_REASON);
            statusReasonControl.setValidators([Validators.required]);
          } else {
            statusReasonControl.setValue(null);
            statusReasonControl.setValidators([Validators.required, reasonNotValid(this.declineReasons)]);
          }
          if (this.medication()) {
            const status = this.medication().guaranteeStatus ? this.medication().guaranteeStatus : this.medication().status;
            if (value !== status) {
              this.statusChanged.set(true);
            } else {
              this.statusChanged.set(false);
              statusReasonControl.clearValidators();
            }
          }
          statusReasonControl.updateValueAndValidity();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchDeclineReasonsChanges() {
    this.fc.statusReason.valueChanges
      .pipe(
        map(value => (value ? value : '')),
        tap((value: string) => {
          this.filteredDeclineReasons.set(this.declineReasons.filter(reason => reason.toLowerCase().includes(value.toLowerCase())));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchEditReasonsChanges() {
    this.fc.editReason.valueChanges
      .pipe(
        map(value => (value ? value : '')),
        tap((value: string) => {
          this.filteredEditReasons.set(this.editReasons.filter(reason => reason.toLowerCase().includes(value.toLowerCase())));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private calculateTreatmentDose() {
    return combineLatest([
      this.calculateDrugsQuantityAndFrequencyChanges().pipe(startWith([this.metadataFc.singleDose.value, this.metadataFc.frequency.value]) ?? null),
      this.calculateDailyDosageAndDrugsDuration().pipe(startWith([this.metadataFc.dailyAmount.value, this.metadataFc.duration.value]) ?? null),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private calculateDrugsQuantityAndFrequencyChanges() {
    return combineLatest([
      this.metadataFc.singleDose.valueChanges.pipe(startWith(this.metadataFc.singleDose.value ?? null)),
      this.metadataFc.frequency.valueChanges.pipe(startWith(this.metadataFc.frequency.value ?? null)),
    ]).pipe(
      filter(([value, frequency]: [number | null, string | null]) => !!value && !!frequency),
      tap(([value, frequency]: [number | null, string | null]) => {
        this.metadataFc.dailyAmount.patchValue(value! * +frequency, {
          emitEvent: true,
          onlySelf: true,
        });
      })
    );
  }

  private watchCompletionCheckboxChanges() {
    return this.fc.completedInAppointment.valueChanges
      .pipe(
        tap(value => {
          this.completionChecked.set(!!value);
          if (value) {
            this.medicationForm.addControl('completionReason', this.fb.control(null, [Validators.required]));
          } else {
            this.medicationForm.removeControl('completionReason');
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private calculateDailyDosageAndDrugsDuration() {
    return combineLatest([
      this.metadataFc.dailyAmount.valueChanges.pipe(startWith(this.metadataFc.dailyAmount.value ?? null)),
      this.metadataFc.duration.valueChanges.pipe(startWith(this.metadataFc.duration.value ?? null)),
    ]).pipe(
      filter(([value, duration]: [number | null, number | null]) => !!value && !!duration),
      tap(([value, duration]: [number | null, number | null]) => {
        this.metadataFc.treatmentDose.patchValue(value! * duration!, {
          emitEvent: true,
          onlySelf: true,
        });
      })
    );
  }

  private saveDraftPrescription() {
    if (this.medication()) {
      return this.updateDraftPrescription();
    } else {
      return this.createDraftPrescription();
    }
  }

  private createDraftPrescription() {
    const formValue = { ...this.medicationForm.getRawValue(), id: crypto.randomUUID(), guaranteeId: this.guaranteeApplicationId() || null };

    const combinedReason = combineReasons({
      editReason: formValue.editReason,
      statusReason: formValue.statusReason,
    });

    this.store.dispatch(addPrescriptionDraft({ draft: { ...formValue, reason: combinedReason } }));
  }

  private updateDraftPrescription() {
    const formValue = { ...this.medicationForm.getRawValue(), id: this.medication().id, guaranteeId: this.guaranteeApplicationId() || null };

    const combinedReason = combineReasons({
      editReason: formValue.editReason,
      statusReason: formValue.statusReason,
    });

    this.store.dispatch(updatePrescriptionDraft({ draft: { ...formValue, reason: combinedReason } }));
  }

  private savePrescriptionForm() {
    return this.medication() ? this.updatePrescription() : this.createPrescription();
  }

  private createPrescription() {
    const formValue = this.medicationForm.getRawValue();

    const editReason = this.completionChecked() ? this.medicationForm.getRawValue().completionReason : '';

    const combinedReason = combineReasons({
      editReason: editReason,
      statusReason: formValue.statusReason,
    });

    return this.prescriptionsService.createPrescription(this.memberId(), this.guaranteeApplicationId(), this.adaptFormValueToRequestBody(formValue, combinedReason));
  }

  private updatePrescription() {
    const formValue = this.medicationForm.getRawValue();

    let editReason: string;
    if (this.completionChecked() && !formValue.editReason) {
      editReason = formValue.completionReason;
    } else {
      editReason = formValue.editReason;
    }

    const combinedReason = combineReasons({
      editReason: editReason,
      statusReason: formValue.statusReason,
    });

    return this.prescriptionsService.updatePrescription(
      this.memberId(),
      this.guaranteeApplicationId(),
      this.medication().id,
      this.adaptFormValueToRequestBody(formValue, combinedReason, this.medication().id)
    );
  }

  private adaptFormValueToRequestBody(formValue: typeof this.medicationForm.value, combinedReason: string, id?: string) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<PharmacologicalItemMetaData>>{
      id: id ? id : crypto.randomUUID(),
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: combinedReason,
      metadata: {
        single_dose: formValue.metadata.singleDose.toString(),
        form: formValue.metadata.form.join(', '),
        frequency: formValue.metadata.frequency,
        treatment_dose: formValue.metadata.treatmentDose.toString(),
        daily_amount: formValue.metadata.dailyAmount.toString(),
        duration: formValue.metadata.duration.toString(),
        inn: formValue.metadata.inn.toUpperCase(),
        inn_ua: formValue.metadata.morionSubstance,
        trademarks: JSON.stringify(adaptPlainObjectToCamelCase(formValue.metadata.morionItems)),
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      originalPrescriptionId: formValue.originalPrescriptionId,
    };
  }

  protected getEndDate(prescriptionQuantity: number = 1): Date {
    const daysAdded: number = MEDICATION_MIN_PERIOD * prescriptionQuantity;
    const validStartDate = this.fc ? this.fc.startDate.value : this.startDate;
    this.endDate = new Date(validStartDate); // Reset date to start date
    this.endDate.setDate(this.endDate.getDate() + daysAdded); // Add days
    this.endDate.setHours(23, 59, 59, 999); // Set time to end of the day
    return this.endDate;
  }

  protected submitPrescription(): void {
    this.medicationForm.markAllAsTouched();
    this.medicationForm.updateValueAndValidity();

    if (this.medicationForm.valid && (this.statusChanged() || this.dataChanged() || this.completionChecked())) {
      this.loading.set(true);
      this.savePrescriptionForm()
        .pipe(take(1))
        .subscribe({
          next: result => this.dialogRef.close(result),
          error: error => {
            console.error('Error:', error);
            this.loading.set(false);
          },
        });
    }
  }

  protected submitDraft(): void {
    this.medicationForm.markAllAsTouched();
    this.medicationForm.updateValueAndValidity();

    if (this.medicationForm.valid && (this.statusChanged() || this.dataChanged())) {
      this.loading.set(true);
      this.saveDraftPrescription();
      this.closeDialog();
    }
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }
}
